/**
 * Picker de canales de Slack de los formularios del Brain (19/10/2026): buscador + lista de
 * los canales públicos del workspace (`brain.listSlackChannels`) más los que el caller
 * suma arriba (`extra`: los ya registrados del cliente, privados o externos incluidos), chips
 * de lo elegido. `single` elige uno (canal de alertas, salida del status). Antes vivía
 * dentro del Step2 del alta de cliente.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Search, X } from 'lucide-react';
import { trpc } from '@/lib/trpc';

export interface PickerChannel {
  id: string;
  name: string;
  /** Texto chico a la derecha ("del cliente", "externo"). */
  hint?: string;
}

export function SlackChannelPicker({ selected, onChange, extra = [], exclude = [], knownNames = {}, single = false, listClassName = 'max-h-44' }: {
  selected: string[];
  /** Ids elegidos + nombre de cada uno (lo que el form guarda para el PR). */
  onChange: (ids: string[], names: Record<string, string>) => void;
  extra?: PickerChannel[];
  /** Ids que no se ofrecen (ya usados en otra lista del mismo form). */
  exclude?: string[];
  /** Nombres que el form ya conoce (seleccionados de una propuesta o de un borrador): se muestran aunque no estén en la lista pública. */
  knownNames?: Record<string, string>;
  single?: boolean;
  listClassName?: string;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const { data, isLoading } = trpc.brain.listSlackChannels.useQuery(undefined, { staleTime: 5 * 60_000 });

  const all = useMemo(() => {
    const byId = new Map<string, PickerChannel>();
    for (const c of extra) byId.set(c.id, c);
    for (const c of data?.channels ?? []) if (!byId.has(c.id)) byId.set(c.id, { id: c.id, name: c.name });
    for (const [id, name] of Object.entries(knownNames)) if (!byId.has(id)) byId.set(id, { id, name });
    return [...byId.values()];
  }, [data, extra, knownNames]);
  const names = useMemo(() => Object.fromEntries(all.map((c) => [c.id, c.name])), [all]);
  const excluded = new Set(exclude);
  const q = search.trim().toLowerCase();
  const filtered = all.filter((c) => !excluded.has(c.id) && (!q || c.name.toLowerCase().includes(q) || c.id.toLowerCase() === q));
  const selectedSet = new Set(selected);

  // Solo los nombres que conocemos: un canal privado que no está en la lista pública no se pisa con su id.
  const emit = (ids: string[]) => onChange(ids, Object.fromEntries(ids.filter((id) => names[id]).map((id) => [id, names[id]])));
  const toggle = (id: string) => {
    if (single) { emit(selectedSet.has(id) ? [] : [id]); return; }
    emit(selectedSet.has(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  return (
    <div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selected.map((id) => (
            <span key={id} className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#FF6B6B]/10 border border-[#FF6B6B]/20 text-[#FF6B6B] font-medium">
              #{names[id] ?? id}
              <button type="button" onClick={() => toggle(id)} className="ml-0.5 hover:opacity-60 transition-opacity" aria-label={t('brain.clients.remove_channel_aria', { name: names[id] ?? id })}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="rounded-lg border border-border overflow-hidden">
        <div className="relative border-b border-border">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('brain.clients.step2.search_placeholder')}
            className="w-full pl-8 pr-3 py-2 text-sm bg-transparent focus:outline-hidden"
          />
        </div>
        <div className={`${listClassName} overflow-y-auto`}>
          {isLoading && <p className="text-xs text-muted-foreground text-center py-6">{t('brain.clients.step2.loading')}</p>}
          {!isLoading && filtered.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">{t('brain.clients.step2.no_results')}</p>}
          {filtered.map((c) => {
            const on = selectedSet.has(c.id);
            return (
              <button key={c.id} type="button" onClick={() => toggle(c.id)}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-muted/50 transition-colors text-left ${on ? 'bg-[#FF6B6B]/5' : ''}`}>
                <span className={on ? 'text-[#FF6B6B] font-medium' : 'text-gray-700'}>
                  #{c.name}{c.hint && <span className="ml-2 text-[11px] text-gray-400 font-normal">{c.hint}</span>}
                </span>
                {on && <CheckCircle2 className="w-3.5 h-3.5 text-[#FF6B6B] shrink-0" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
